import React, { useEffect } from "react";
import { connect } from "react-redux";
import Head from "next/head";
import _get from "lodash/get";
import getConfig from "next/config";
import classNames from "classnames";

import { resizeImage } from "@lib/helpers";
import Breadcrumbs from "@components/Breadcrumbs";
import ContentEditorView from "@components/ContentEditorView/index";
import Subcategories from "@components/Subcategories";
import ScrollableSubcategories from "@components/ScrollableSubcategories";
import ProductList from "@components/ProductList";
import NoSearchResultsRecommendations from "@components/NoSearchResultsRecommendations";
import Filters from "@components/Filters";
import ActiveFilters from "@components/Filters/ActiveFilters";
import Sort from "@components/Sort";
import { addModal, removeModal } from "@store/app/actions";
import { trackProductClick } from "@store/tracker/actions";
import CompareWidget from "@containers/Catalog/CompareWidget";
import Banners from "@components/Banners";
import Heading from "@components/Heading";
import {
  getCategory,
  getMoreCategory,
  getMoreSearch,
} from "@store/catalog/actions";
import PaginationWithLinks from "@components/PaginationWithLinks";
import FootprintsWebAdsBanner from "@components/Footprints/FootprintsWebAdsBanner";
import { setGTMProductsImpressions } from "@lib/GTMHelper";
import CredexUserPromoBanner from "@components/CredexUserPromoBanner";

const {
  publicRuntimeConfig: { settings },
} = getConfig();

const CatalogList = (props) => {
  const { layout, query, currentUrl, featureFlags = null, user } = props;

  const {
    breadcrumbs = [],
    products,
    filters,
    activeFilters,
    meta,
  } = props.currentCategory;

  // prettier-ignore
  const {
    display_mode = "PRODUCTS",
    meta_title = layout === "search" ? `Rezultate cautare: ${query.q}` : "",
    meta_description = "",
    meta_keywords = "",
    meta_robots,
    seo_canonical,
    seo_alternate_canonical = "",
    seo_title,
    name = layout === "search"
      ? `Rezultate cautare${
          query.q && query.q.length <= 36
            ? ": " + query.q
            : ""
        }`
      : "",
    description = null,
    description_editor_object = null,
    subcategories,
    landing_page,
    banners,
  } = props.currentCategory.category || props.currentCategory.search;

  const showFootprintsBanner = Boolean(
    featureFlags?.enableFootprints &&
      featureFlags?.enableFootprintsBanner &&
      layout !== "search"
  );
  const hasCredexUserPromo = user.customer?.has_promo === "CC12RATE";

  let parsedDescriptionObject;

  if (description_editor_object) {
    try {
      parsedDescriptionObject = JSON.parse(description_editor_object);
    } catch (error) {
      parsedDescriptionObject = false;
    }
  }

  if (layout === "search") {
    breadcrumbs.push({
      name: name,
      id: 99,
      level: 99,
    });
  }

  const handleProductClick = (product, options) => {
    props.trackProductClick(product, options);
  };

  const Meta = () => (
    <Head>
      <title>{meta_title || name || ""}</title>

      {meta_description.length > 0 && (
        <meta name="description" content={meta_description} />
      )}

      {meta_keywords.length > 0 && (
        <meta name="keywords" content={meta_keywords} />
      )}

      {props.currentCategory.category &&
        !!props.currentCategory.category.thumbnail && (
          <meta
            property="og:image"
            content={resizeImage(
              props.currentCategory.category.thumbnail,
              "600x600"
            )}
          ></meta>
        )}

      {meta_robots?.length > 0 && <meta name="robots" content={meta_robots} />}

      {layout === "search" ||
        (seo_canonical && !seo_alternate_canonical?.length > 0 && (
          <link rel="canonical" href={currentUrl} />
        ))}

      {!(layout === "search") &&
        seo_canonical &&
        seo_alternate_canonical?.length > 0 && (
          <link rel="alternate" href={seo_alternate_canonical} />
        )}
    </Head>
  );

  useEffect(() => {
    if (products?.length > 0) {
      setGTMProductsImpressions(
        products.map((product, index) => ({
          ...product,
          index: index + 1,
          ...(layout !== "search"
            ? {
                categoryPath: breadcrumbs,
                item_list_name: breadcrumbs?.[breadcrumbs.length - 1]?.name,
              }
            : { item_list_name: "Rezultate cautare" }),
        }))
      );
    }
  }, [products, breadcrumbs, layout]);

  const Content = () => {
    if (display_mode === "PAGE") {
      return (
        <>
          {landing_page && landing_page.content ? (
            <div
              dangerouslySetInnerHTML={{ __html: landing_page.content }}
            ></div>
          ) : null}
          <Subcategories subcategories={subcategories} />

          {showFootprintsBanner && <FootprintsWebAdsBanner />}
        </>
      );
    } else {
      return (
        <>
          {subcategories && subcategories.length > 0 && (
            <ScrollableSubcategories subcategories={subcategories} />
          )}
          <div>
            <ActiveFilters activeFilters={activeFilters} />
            <Sort
              addModal={props.addModal}
              removeModal={props.removeModal}
              urlPath={props.urlPath}
              toolbar={props.currentCategory.toolbar}
              params={props.extractedFiltersParams.otherParams}
              meta={props.currentCategory.meta}
            />

            <div className="lg-u-display-none">
              <div className="Filters-mobile-active js-mobile-active-filters"></div>
            </div>

            <ProductList
              products={products.map((product, index) => ({
                ...product,
                index: index + 1,
                ...(layout !== "search"
                  ? {
                      categoryPath: breadcrumbs,
                      item_list_name:
                        breadcrumbs?.[breadcrumbs.length - 1]?.name,
                    }
                  : { item_list_name: "Rezultate cautare" }),
              }))}
              filter={props.extractedFiltersParams}
              containerClass="Products flex flex-wrap relative"
              onProductClick={handleProductClick}
              listType={layout}
            />

            {_get(props, "currentCategory.toolbar.paginationBottom", [])
              .length > 1 && (
              <div className="mb-6">
                <div className="flex justify-center">
                  <PaginationWithLinks
                    pagination={props.currentCategory.toolbar.paginationBottom}
                  />
                </div>

                <div className="text-center mt-1">
                  {props.currentCategory.meta.items === 1
                    ? `${props.currentCategory.meta.items} produs`
                    : `${props.currentCategory.meta.items} produse`}
                </div>
              </div>
            )}

            <Sort
              addModal={props.addModal}
              removeModal={props.removeModal}
              urlPath={props.urlPath}
              toolbar={props.currentCategory.toolbar}
              params={props.extractedFiltersParams.otherParams}
              meta={props.currentCategory.meta}
              modal={true}
            />
          </div>
        </>
      );
    }
  };

  const ListLayout = (props) => {
    if (display_mode !== "PAGE") {
      const totalProducts = meta?.items || 0;

      return products && products.length > 0 ? (
        <div className="lg:flex">
          <Filters
            activeFilters={activeFilters}
            filters={filters}
            extractedFiltersParams={props.extractedFiltersParams}
            query={query}
            totalProducts={totalProducts}
            isInSearch={layout && layout === "search"}
          />

          <div className={`${products.length !== 0 ? "lg:w-4/5" : "lg:mt-8"} `}>
            {hasCredexUserPromo && <CredexUserPromoBanner />}

            {display_mode !== "PAGE" && (
              <div
                className={classNames("block md:flex md:items-baseline", {
                  "px-[16px] py-[8px]": settings.websiteName === "bricostore",
                })}
              >
                <Heading
                  as="h1"
                  title={seo_title || name}
                  display="h1"
                  sameSizeOnMobile
                />

                {totalProducts !== 0 && (
                  <div
                    className={classNames(
                      "md:ml-2 font-normal text-22px leading-7",
                      {
                        "text-gray-light":
                          settings.websiteName !== "bricostore",
                        "text-[#9F9FA9]": settings.websiteName === "bricostore",
                      }
                    )}
                  >
                    ({totalProducts}{" "}
                    {totalProducts === 1 ? "produs" : "produse"})
                  </div>
                )}
              </div>
            )}

            {showFootprintsBanner && <FootprintsWebAdsBanner />}

            {props.children}
          </div>
        </div>
      ) : (
        <div className="flex flex-col md:flex-row items-center justify-center md:justify-start gap-6 my-4">
          <img
            className="w-[158px]"
            src={`${settings.websiteStatics}/images/cart/empty-cart.svg`}
            alt=""
          />

          <Heading
            title={
              seo_title || layout === "search" ? (
                <span>
                  Ne pare rau, cautarea ta <strong>"{query?.q ?? ""}"</strong>{" "}
                  nu a avut niciun rezultat. Verifica daca ai scris corect sau
                  incearca sa cauti folosind alt termen.
                </span>
              ) : (
                <span>
                  Nu au fost gasite produse conform criteriilor selectate.
                </span>
              )
            }
            as="h1"
            display="h6"
            classesProp="max-w-[639px]"
          />
        </div>
      );
    }

    return props.children;
  };

  return (
    <>
      <Meta />

      <Breadcrumbs breadcrumbs={breadcrumbs} />

      {products !== undefined &&
        products.length > 0 &&
        display_mode === "PAGE" && (
          <Heading as="h1" title={seo_title || name} display="h1" />
        )}

      <ListLayout
        extractedFiltersParams={props.extractedFiltersParams}
        urlPath={props.urlPath}
        fetchCurrentCategory={props.fetchCurrentCategory}
      >
        {banners && (
          <>
            {banners[0].images_mobile && banners[0].images_mobile.length !== 0 && (
              <div className="sm:hidden">
                <Banners items={banners[0].images_mobile} />
              </div>
            )}
            {banners[0].images_tablet && banners[0].images_tablet.length !== 0 && (
              <div className="hidden sm:block lg:hidden">
                <Banners items={banners[0].images_tablet} />
              </div>
            )}
            {banners[0].images && banners[0].images.length !== 0 && (
              <div className="hidden lg:block">
                <Banners items={banners[0].images} />
              </div>
            )}
          </>
        )}
        {landing_page && landing_page.content ? (
          <div dangerouslySetInnerHTML={{ __html: landing_page.content }}></div>
        ) : null}
        {<Content />}

        {/* SEO page descriptions
        - should only appear on list pages without filters or sorting applied
        - they should not appear on search results page */}
        {props.extractedFiltersParams &&
          props.extractedFiltersParams.filters &&
          Object.keys(props.extractedFiltersParams.filters).length === 0 &&
          props.extractedFiltersParams.otherParams &&
          Object.keys(props.extractedFiltersParams.otherParams).length ===
            0 && (
            <>
              {parsedDescriptionObject ? (
                <ContentEditorView content={parsedDescriptionObject} />
              ) : (
                description?.length > 0 && (
                  <div
                    className="my-10"
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                )
              )}
            </>
          )}
      </ListLayout>

      {layout &&
        layout === "search" &&
        (!products || products.length === 0) && (
          <NoSearchResultsRecommendations onProductClick={handleProductClick} />
        )}
      <CompareWidget />
    </>
  );
};

export default connect(
  (state) => ({
    currentCategory: state.catalog.currentCategory,
    urlPath: state.catalog.urlPath,
    user: state.user,
    extractedFiltersParams: state.catalog.extractedFiltersParams,
    featureFlags: state.app.websiteAdminConfigs.featureFlagsWebsite,
  }),
  {
    addModal,
    removeModal,
    trackProductClick,
    getCategory,
    getMoreCategory,
    getMoreSearch,
  }
)(CatalogList);
